/**
 * Special Events Utility
 * 
 * Determines if the current date falls within a special event period
 * that warrants gift-focused messaging (Black Friday, Christmas).
 * 
 * This utility is used for:
 * - Dynamic search bar placeholders
 * - SEO metadata enhancement
 * - Event-aware UI modifications
 * 
 * Note: This does NOT trigger content regeneration for all events.
 * Christmas uses existing content with enhanced placeholders/SEO only.
 */

import { LocaleDetails } from 'shared/src/types/languages.type';

/**
 * Days before Black Friday that its content is allowed to go live.
 *
 * Deliberately one value for every environment. This used to read
 * `NODE_ENV === 'production' ? 18 : 100`, which opened the window ~3.5 months
 * early everywhere *except* production. That is not a harmless dev nicety: a
 * local page view runs `applySpecialMoment`, which generates event copy and
 * writes it back to the shared database, so the dev-only branch published
 * out-of-season Black Friday content for real pages.
 *
 * To rehearse an event, set SPECIAL_EVENT_LEAD_DAYS (or the client-visible
 * NEXT_PUBLIC_SPECIAL_EVENT_LEAD_DAYS) explicitly. Both are unset by default,
 * local dev included, so the season is never entered by accident.
 */
export const BLACK_FRIDAY_LEAD_DAYS = 18;

/**
 * Resolve the lead time for an event, honouring the explicit test override.
 *
 * Returns `fallbackDays` unless the override parses to a non-negative finite
 * number, so a typo or a stray empty string can never widen the window.
 */
export function getSpecialEventLeadDays(fallbackDays: number): number {
  const raw =
    process.env.NEXT_PUBLIC_SPECIAL_EVENT_LEAD_DAYS ?? process.env.SPECIAL_EVENT_LEAD_DAYS;
  if (!raw) {
    return fallbackDays;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallbackDays;
}

export type SpecialEventType = 'black-friday' | 'christmas' | null;

export interface SpecialEventInfo {
  type: SpecialEventType;
  isGiftEvent: boolean;
  name: string;
  /** Whether this event should trigger expensive content regeneration */
  regenerateContent: boolean;
}

/**
 * Event name translations for different locales
 */
const eventNames: Record<string, Record<string, string>> = {
  blackFriday: {
    en: 'Black Friday',
    fr: 'Vendredi fou',
    de: 'Black Friday',
    es: 'Black Friday',
    it: 'Black Friday',
    'pt-BR': 'Black Friday',
    'pt-PT': 'Black Friday',
  },
  christmas: {
    en: 'Christmas',
    fr: 'Noël',
    de: 'Weihnachten',
    es: 'Navidad',
    it: 'Natale',
    'pt-BR': 'Natal',
    'pt-PT': 'Natal',
  },
};

/**
 * Checks if the current date falls within a special event period
 * 
 * @param locale - User's locale details
 * @returns Special event info or null if no active event
 */
export function getSpecialEvent(locale: LocaleDetails): SpecialEventInfo | null {
  const now = new Date();
  const year = now.getFullYear();
  const lang = locale.languageCode;

  // Check Black Friday (14-18 days before event through event day)
  const blackFriday = getBlackFridayDate(year);
  const blackFridayStart = new Date(blackFriday);
  blackFridayStart.setDate(blackFridayStart.getDate() - getSpecialEventLeadDays(BLACK_FRIDAY_LEAD_DAYS));
  const blackFridayEnd = new Date(blackFriday.getFullYear(), blackFriday.getMonth(), blackFriday.getDate(), 23, 59, 59, 999);

  if (now >= blackFridayStart && now <= blackFridayEnd) {
    return {
      type: 'black-friday',
      isGiftEvent: true,
      name: `${eventNames.blackFriday[lang]} ${year}`,
      regenerateContent: true, // Black Friday regenerates content
    };
  }

  // Check Christmas (December 1-25)
  const christmasStart = new Date(year, 11, 1); // December 1
  const christmasEnd = new Date(year, 11, 25, 23, 59, 59, 999); // December 25

  if (now >= christmasStart && now <= christmasEnd) {
    return {
      type: 'christmas',
      isGiftEvent: true,
      name: `${eventNames.christmas[lang]} ${year}`,
      regenerateContent: false, // Christmas does NOT regenerate content (cost-saving)
    };
  }

  return null;
}

/**
 * Checks if we're currently in a gift-giving event period
 * Simpler helper for components that just need a boolean check
 */
export function isGiftEventPeriod(locale: LocaleDetails): boolean {
  const event = getSpecialEvent(locale);
  return event !== null && event.isGiftEvent;
}

// Helper: Calculate Black Friday date (day after Thanksgiving)
function getBlackFridayDate(year: number): Date {
  const thanksgiving = getThanksgivingDate(year);
  return new Date(thanksgiving.getFullYear(), thanksgiving.getMonth(), thanksgiving.getDate() + 1);
}

// Helper: Get Thanksgiving date (4th Thursday of November)
function getThanksgivingDate(year: number): Date {
  return getNthWeekdayOfMonth(year, 10, 4, 4); // Month 10 = November, weekday 4 = Thursday, 4th occurrence
}

// Helper: Get nth weekday of a month
function getNthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): Date {
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  const day = 1 + ((7 + weekday - firstWeekday) % 7) + (n - 1) * 7;
  return new Date(year, month, day);
}




